import { type DropEvent, type FileRejection, Dropzone as LouisDropzone } from '@lokalise/louis';
import { type DragEvent, useState } from 'react';

import { DROPZONE_ACCEPTED_FILE_TYPES, DROPZONE_FILE_MAX_SIZE_BYTES } from '../../constants';
import { type RejectedFile } from '../../model/rejectedFile';
import ActiveState from './ActiveState';
import AdditionalFilesIdleState from './AdditionalFilesIdleState';
import ErrorState from './ErrorState';
import InitialFilesIdleState from './InitialFilesIdleState';
import { DropzoneContainer } from './style';

const toRejectedFile = ({ file, errors }: FileRejection): RejectedFile => {
    if (errors.some((error) => error.code === 'file-invalid-type')) {
        return { file, problem: 'file-invalid-type' };
    }

    if (errors.some((error) => error.code === 'file-too-large')) {
        return { file, problem: 'file-too-large' };
    }

    return { file, problem: 'unknown' };
};

type DropzoneProps = {
    additional?: boolean;
    onDrop?: (acceptedFiles: File[], rejectedFiles: RejectedFile[], event: DropEvent) => void;
};

export const Dropzone = ({ additional, onDrop }: DropzoneProps) => {
    const [filesLength, setFilesLength] = useState(0);

    const onDragEnter = (event: DragEvent<Element>) => setFilesLength(event.dataTransfer.items.length);

    return (
        <LouisDropzone
            maxSize={DROPZONE_FILE_MAX_SIZE_BYTES}
            accept={DROPZONE_ACCEPTED_FILE_TYPES}
            onDragEnter={onDragEnter}
            onDrop={(acceptedFiles, rejectedFiles, event) =>
                onDrop?.(acceptedFiles, rejectedFiles.map(toRejectedFile), event)
            }
        >
            {({ isDragActive, isDragAccept, isDragReject, getRootProps }) => (
                <DropzoneContainer
                    $additional={additional}
                    {...getRootProps()}
                    $isDragReject={isDragReject}
                    $isDragAccept={isDragAccept}
                >
                    {isDragAccept && <ActiveState fileCount={filesLength} />}
                    {isDragReject && <ErrorState />}
                    {!isDragActive &&
                        !isDragReject &&
                        (additional ? <AdditionalFilesIdleState /> : <InitialFilesIdleState />)}
                </DropzoneContainer>
            )}
        </LouisDropzone>
    );
};

export default Dropzone;
